import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, Activity, Stethoscope, Wrench, GraduationCap, Trophy, MapPin, Building2 } from "lucide-react";

const KPIS = [
  { key: "total_mechanics", label: "Total Mechanics", icon: Users, tid: "kpi-mechanics" },
  { key: "daily_active_users", label: "DAU (24h)", icon: Activity, tid: "kpi-dau" },
  { key: "diagnostics_asked", label: "Diagnostics Asked", icon: Stethoscope, tid: "kpi-diagnostics" },
  { key: "job_cards_created", label: "Job Cards", icon: Wrench, tid: "kpi-jobcards" },
  { key: "lessons_completed", label: "Lessons Completed", icon: GraduationCap, tid: "kpi-lessons" },
  { key: "rewards_issued", label: "Rewards Issued", icon: Trophy, tid: "kpi-rewards" },
  { key: "cities_active", label: "Cities Active", icon: MapPin, tid: "kpi-cities" },
  { key: "workshops", label: "Workshops", icon: Building2, tid: "kpi-workshops" },
];

function Sparkbar({ value, max }) {
  const pct = Math.min(100, (value / Math.max(max, 1)) * 100);
  return (
    <div className="h-1 bg-zinc-900 mt-2">
      <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [levels, setLevels] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/stats/overview").then((r) => setStats(r.data)),
      api.get("/stats/level-distribution").then((r) => setLevels(r.data)),
      api.get("/stats/city-activity").then((r) => setCities(r.data)),
    ]).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="p-8">
        <div className="overline">// loading overview</div>
        <div className="font-mono-data mt-2"><span className="ascii-load" /></div>
      </div>
    );
  }

  const maxLevel = Math.max(...levels.map((l) => l.count), 1);

  return (
    <div className="p-8 space-y-8" data-testid="dashboard-page">
      <header>
        <div className="overline">// admin / overview</div>
        <h1 className="font-display font-black text-3xl tracking-tight mt-1">
          Platform Overview
        </h1>
        <p className="text-zinc-500 text-sm mt-1">
          Real-time pulse of mechanics, diagnostics, repairs, and learning across India.
        </p>
      </header>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-grid">
        {KPIS.map(({ key, label, icon: Icon, tid }) => (
          <div key={key} data-testid={tid} className="gs-card p-4">
            <div className="flex items-center justify-between">
              <div className="overline">{label}</div>
              <Icon className="w-4 h-4 text-zinc-600" strokeWidth={2} />
            </div>
            <div className="font-mono-data font-bold text-3xl mt-3 text-zinc-100">
              {(stats[key] ?? 0).toLocaleString("en-IN")}
            </div>
            <Sparkbar value={stats[key] ?? 0} max={Math.max(stats.total_mechanics * 4, 200)} />
          </div>
        ))}
      </div>

      {/* Lower row: Level dist + Cities + Recent Qs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Skill levels */}
        <div className="gs-card p-5">
          <div className="overline mb-3">Skill Level Distribution</div>
          <div className="space-y-3">
            {levels.map((l) => (
              <div key={l.level} data-testid={`level-row-${l.level.replace(/\s/g, '-').toLowerCase()}`}>
                <div className="flex justify-between text-sm">
                  <span className={`px-1.5 py-0.5 text-[10px] font-display font-bold border level-${l.level.toLowerCase().replace(/\s/g, '-').replace('certified-pro', 'pro')}`}>
                    {l.level.toUpperCase()}
                  </span>
                  <span className="font-mono-data text-zinc-300">{l.count}</span>
                </div>
                <div className="h-1.5 bg-zinc-900 mt-1.5">
                  <div className="h-full bg-amber-500" style={{ width: `${(l.count / maxLevel) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cities */}
        <div className="gs-card p-5">
          <div className="overline mb-3">Active Cities</div>
          <div className="space-y-2">
            {cities.map((c) => (
              <div key={c.city} className="flex justify-between items-center text-sm border-b border-zinc-900 pb-1.5">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-amber-500/70" />
                  <span>{c.city}</span>
                </div>
                <span className="font-mono-data text-zinc-400 text-xs">{c.count} mech</span>
              </div>
            ))}
            {cities.length === 0 && <div className="text-zinc-600 text-xs">No cities yet.</div>}
          </div>
        </div>

        {/* Recent questions */}
        <div className="gs-card p-5">
          <div className="overline mb-3">Recent Diagnostic Queries</div>
          <div className="space-y-3">
            {stats.recent_questions.map((q) => (
              <Link key={q.id} to={`/mechanics/${q.mechanic_id}`} className="block hover:bg-zinc-900/40 p-2 -m-2 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm text-zinc-200 truncate">{q.query_text}</div>
                  {q.confidence ? <span className="confidence-badge whitespace-nowrap">{q.confidence}%</span> : null}
                </div>
                <div className="text-[11px] text-zinc-600 mt-1 flex gap-2 items-center flex-wrap">
                  <span>{q.mechanic_name}</span>
                  {q.car_number && <span className="font-mono-data text-amber-300 border border-amber-500/40 px-1 bg-amber-500/10">{q.car_number}</span>}
                  {q.dtc_code && <span className="dtc-badge">{q.dtc_code}</span>}
                </div>
              </Link>
            ))}
            {stats.recent_questions.length === 0 && <div className="text-zinc-600 text-xs">No queries yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
